<template>
  <div class="main-page">
    <template v-if="showHeader">
      <section class="page-section">
        <div class="container-fluid">
          <div class="grid justify-content-between">
            <div class="col">
              <div class="">
                <div class="text-3xl font-bold">Assign Role Permission</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <hr />
    </template>
    <section class="page-section pt-3">
      <div class="container-fluid">
        <div class="grid">
          <div class="md:col-9 sm:col-12 comp-grid">
            <div class="">
              <div class="">
                <div>
                  <form
                    ref="observer"
                    tag="form"
                    @submit.prevent="submitForm()"
                    class=""
                    :class="{ card: !isSubPage }"
                  >
                    <div class="grid">
                      <div class="col-12 md:col-12">
                        <div class="mb-2 font-bold text-sm">Role *</div>
                        <div id="ctrl-role_id-holder">
                          <api-data-source
                            api-path="components_data/role_id_option_list"
                          >
                            <template v-slot="req">
                              <Dropdown
                                class="w-full"
                                :class="getErrorClass('role_id')"
                                :loading="req.loading"
                                optionLabel="label"
                                optionValue="value"
                                ref="ctrlrole_id"
                                v-model="formData.role_id"
                                :options="req.response"
                                label="Role"
                                placeholder="Select ..."
                              >
                              </Dropdown>
                              <small
                                v-if="isFieldValid('role_id')"
                                class="p-error"
                                >{{ getFieldError("role_id") }}</small
                              >
                            </template>
                          </api-data-source>
                        </div>
                      </div>
                      <div class="col-12 md:col-12">
                        <div class="mb-2 font-bold text-sm">
                          Permissions (Page Access) *
                        </div>
                        <div id="ctrl-permission-holder">
                          <api-data-source
                            api-path="components_data/permission_option_list"
                          >
                            <template v-slot="req">
                              <MultiSelect
                                class="w-full"
                                :class="getErrorClass('permission')"
                                optionLabel="label"
                                optionValue="value"
                                ref="ctrlpermission"
                                v-model="formData.permission"
                                :options="req.response"
                                label="Permission"
                                :filter="true"
                              >
                                <template #option="slotProps">
                                  <div class="flex align-items-center">
                                    <div>
                                      <div>{{ slotProps.option.label }}</div>
                                      <div
                                        v-if="slotProps.option.caption"
                                        class="text-sm text-500"
                                      >
                                        {{ slotProps.option.caption }}
                                      </div>
                                    </div>
                                  </div>
                                </template>
                              </MultiSelect>
                              <small
                                v-if="isFieldValid('permission')"
                                class="p-error"
                                >{{ getFieldError("permission") }}</small
                              >
                            </template>
                          </api-data-source>
                        </div>
                      </div>
                    </div>
                    <div v-if="showSubmitButton" class="text-center my-3">
                      <Button
                        class="p-button-primary p-button-rounded"
                        type="submit"
                        label="Submit"
                        icon="pi pi-send"
                        :loading="saving"
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import { computed, reactive, toRefs, onMounted } from "vue";
import useVuelidate from "@vuelidate/core";
import { required, numeric } from "@/services/validators";
import { useApp } from "@/composables/app.js";
import { useAddPage } from "@/composables/addpage.js";
//[--PAGE-IMPORT-STATEMENT--]
const props = defineProps({
  pageName: {
    type: String,
    default: "user_permissions",
  },
  routeName: {
    type: String,
    default: "user_permissionsadd",
  },
  apiPath: {
    type: String,
    default: "user_permissions/add",
  },
  submitButtonLabel: {
    type: String,
    default: "Submit",
  },
  formValidationError: {
    type: String,
    default: "Form is invalid",
  },
  formValidationMsg: {
    type: String,
    default: "Please complete the form",
  },
  msgAfterSave: {
    type: String,
    default: "Record added successfully",
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  showSubmitButton: {
    type: Boolean,
    default: true,
  },
  redirect: {
    type: Boolean,
    default: true,
  },
  isSubPage: {
    type: Boolean,
    default: false,
  },
  pageData: {
    // use to set formData default values from another page
    type: Object,
    default: () => {},
  },
});
const app = useApp();
const formDefaultValues = {
  role_id: "",
  permission: [],
};
const formData = reactive({ ...formDefaultValues });
//form validation rules
const rules = computed(() => {
  return {
    role_id: { required, numeric },
    permission: { required },
  };
});
const v$ = useVuelidate(rules, formData); // form validation
const page = useAddPage({ props, formData, v$, beforeSubmit, afterSubmit });
// perform custom validation before submit form
// set custom form fields
// return false if validation fails
function beforeSubmit() {
  return true;
}
// after form submited to api
// reset form data.
// redirect to another page
function afterSubmit(response) {
  app.flashMsg(props.msgAfterSave);
  page.setFormDefaultValues(); //reset form data
  if (props.redirect) app.navigateTo(`/user_roles/view/${formData.role_id}`);
}
//page state
const {
  submitted, // form submitted state - Boolean
  saving, // form api submiting state - Boolean
  pageReady, // if api data loaded successfully
} = toRefs(page.state);
//page methods
const {
  submitForm, //submit form data to api
  getErrorClass, // return error class if field is invalid- args(fieldname)
  getFieldError, //  get validation error message - args(fieldname)
  isFieldValid, // check if field is validated - args(fieldname)
  // map api datasource  to Select options label-value
} = page.methods;
onMounted(() => {
  const pageTitle = "Add Role Permissions";
  app.setPageTitle(props.routeName, pageTitle); // set browser page title
});
// expose page object for other components access
defineExpose({
  page,
});
</script>
<style scoped></style>
<style scoped></style>
